import "./Modals.scss";
import { useState } from "react";
import { Button, Checkbox, Input, List, Modal, Tag, Typography } from "antd";
import { CheckOutlined, CloseOutlined, DownloadOutlined } from "@ant-design/icons";
import { LeaveRequest, ModalProps } from "../../utils/types";
import { apiClient } from "../../utils/httpClient";
import { getErrorMessage, Notify } from "../../utils/constants";
import { formatRequestedAt } from "../../utils/helpers";

type DocumentReviewModalProps = ModalProps & {
  request: LeaveRequest | null;
  onDone?: () => void;
};

const reviewStatusColor: Record<string, string> = {
  PENDING: "default",
  APPROVED: "green",
  REJECTED: "red",
};

const DocumentReviewModal = ({
  isOpen,
  setIsOpen,
  request,
  onDone,
}: DocumentReviewModalProps) => {
  const [comment, setComment] = useState("");
  const [finalReject, setFinalReject] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const documents = request?.documents ?? [];
  const latestDocument = documents[0] ?? null;

  const close = () => {
    setComment("");
    setFinalReject(false);
    setIsOpen(false);
  };

  const submit = async (approved: boolean) => {
    if (!latestDocument) return;
    if (!approved && !comment.trim()) {
      Notify("error", "A comment is required when rejecting a document.");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await apiClient.post(
        `${process.env.REACT_APP_API_URL}/leave-requests/documents/review`,
        {
          documentId: latestDocument.id,
          approved,
          finalReject: approved ? false : finalReject,
          comment,
        },
      );
      Notify("success", response.data ?? "Document review submitted.");
      close();
      if (onDone) onDone();
    } catch (error) {
      console.error("Failed to review document:", error);
      Notify("error", getErrorMessage(error, "Could not submit the document review."));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      title="Review document"
      open={isOpen}
      onCancel={close}
      footer={[
        <Button key="cancel" onClick={close}>
          Cancel
        </Button>,
        <Button
          key="reject"
          danger
          icon={<CloseOutlined />}
          loading={isSubmitting}
          disabled={!latestDocument}
          onClick={() => submit(false)}
        >
          Reject document
        </Button>,
        <Button
          key="approve"
          type="primary"
          icon={<CheckOutlined />}
          loading={isSubmitting}
          disabled={!latestDocument}
          onClick={() => submit(true)}
        >
          Approve document
        </Button>,
      ]}
      width={600}
    >
      {request && (
        <Typography.Paragraph type="secondary">
          {request.employeeName} - Blood donation on {request.eventDate ?? "-"}
        </Typography.Paragraph>
      )}

      {latestDocument ? (
        <div style={{ marginBottom: 16 }}>
          <Typography.Text strong>Current document</Typography.Text>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
            <a
              href={latestDocument.fileBase64}
              download={latestDocument.fileName}
              target="_blank"
              rel="noreferrer"
            >
              <Button icon={<DownloadOutlined />} size="small">
                {latestDocument.fileName || "Download"}
              </Button>
            </a>
            <Tag color={reviewStatusColor[latestDocument.reviewStatus]}>
              {latestDocument.reviewStatus}
            </Tag>
            <Typography.Text type="secondary">
              {formatRequestedAt(latestDocument.uploadedAt)}
            </Typography.Text>
          </div>
        </div>
      ) : (
        <Typography.Paragraph type="warning">
          No document has been uploaded yet.
        </Typography.Paragraph>
      )}

      <Input.TextArea
        placeholder="Comment (required when rejecting)"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        rows={3}
        style={{ marginBottom: 8 }}
      />

      <Checkbox
        checked={finalReject}
        onChange={(e) => setFinalReject(e.target.checked)}
      >
        Final rejection - the document doesn&apos;t match the request, reject
        the whole request instead of asking for a re-upload
      </Checkbox>

      {documents.length > 1 && (
        <div style={{ marginTop: 16 }}>
          <Typography.Text strong>Document history</Typography.Text>
          <List
            size="small"
            dataSource={documents}
            renderItem={(doc) => (
              <List.Item>
                <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                  <span>{doc.fileName || "(no file name)"}</span>
                  <span>
                    <Tag color={reviewStatusColor[doc.reviewStatus]}>{doc.reviewStatus}</Tag>
                    <Typography.Text type="secondary">
                      {formatRequestedAt(doc.uploadedAt)}
                    </Typography.Text>
                  </span>
                </div>
              </List.Item>
            )}
          />
        </div>
      )}
    </Modal>
  );
};

export default DocumentReviewModal;
